import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Paper,
  Tabs,
  Tab,
  useTheme,
  IconButton,
  InputAdornment,
  alpha,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { useDropzone } from 'react-dropzone';
import TitleIcon from '@mui/icons-material/Title';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../context/AuthContext';

function NewsForm() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { authAxios } = useAuth();

  // Language tab: 'en' or 'de'
  const [langTab, setLangTab] = useState('en');

  // Separate state per language
  const [titleEn, setTitleEn] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [titleDe, setTitleDe] = useState('');
  const [bodyDe, setBodyDe] = useState('');

  const [previewImage, setPreviewImage] = useState(null);
  const [formErrors, setFormErrors] = useState({
    titleEn: '',
    bodyEn: '',
    titleDe: '',
    bodyDe: '',
    previewImage: '',
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState('');
  const [contentTab, setContentTab] = useState(0); // 0 = Write, 1 = Preview
  const [imageFile, setImageFile] = useState(null);

  // Fetch news item if editing
  useEffect(() => {
    if (id) {
      const fetchNewsItem = async () => {
        setInitialLoading(true);
        try {
          const response = await authAxios.get(`/news/${id}`);
          const newsItem = response.data;

          setTitleEn(newsItem.title_en || '');
          setBodyEn(newsItem.body_en || '');
          setTitleDe(newsItem.title_de || '');
          setBodyDe(newsItem.body_de || '');

          if (newsItem.image_url) {
            setPreviewImage(newsItem.image_url);
          }
        } catch (err) {
          console.error('Error fetching news item:', err);
          setError('Failed to load news item. Please try again.');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchNewsItem();
    }
  }, [id, authAxios]);

  const handleLangTabChange = (event, newValue) => {
    setLangTab(newValue);
  };

  const handleContentTabChange = (event, newValue) => {
    setContentTab(newValue);
  };

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
    },
    maxFiles: 1,
  });

  /**
   * Validate form.
   * - For publish (isDraft = false): require everything (current behavior).
   * - For draft (isDraft = true): allow partial, but require at least some content.
   */
  const validateForm = (isDraft = false) => {
    let isValid = true;

    const errors = {
      titleEn: '',
      bodyEn: '',
      titleDe: '',
      bodyDe: '',
      previewImage: '',
    };

    const missing = [];

    if (isDraft) {
      // 🔹 DRAFT VALIDATION
      // More relaxed: require at least one language filled with some content
      const hasAnyTitle = titleEn.trim() || titleDe.trim();
      const hasAnyBody = bodyEn.trim() || bodyDe.trim();

      if (!hasAnyTitle) {
        errors.titleEn = 'At least one title (EN or DE) is required for a draft.';
        errors.titleDe = 'At least one title (EN or DE) is required for a draft.';
        missing.push('English or German title');
        isValid = false;
      }

      if (!hasAnyBody) {
        errors.bodyEn = 'At least some content (EN or DE) is required for a draft.';
        errors.bodyDe = 'At least some content (EN or DE) is required for a draft.';
        missing.push('English or German content');
        isValid = false;
      }
      // Image is OPTIONAL for draft → no check
    } else {
      // 🔹 PUBLISH VALIDATION (strict, current behavior)
      // English
      if (!titleEn.trim()) {
        errors.titleEn = 'English title is required';
        missing.push('English title');
        isValid = false;
      }
      if (!bodyEn.trim()) {
        errors.bodyEn = 'English content is required';
        missing.push('English content');
        isValid = false;
      }

      // German
      if (!titleDe.trim()) {
        errors.titleDe = 'German title is required';
        missing.push('German title');
        isValid = false;
      }
      if (!bodyDe.trim()) {
        errors.bodyDe = 'German content is required';
        missing.push('German content');
        isValid = false;
      }

      // Image required for publish
      if (!previewImage) {
        errors.previewImage = 'Image is required';
        missing.push('News image');
        isValid = false;
      }
    }

    setFormErrors(errors);

    if (!isValid) {
      // Switch to the most relevant language tab
      if (errors.titleEn || errors.bodyEn) {
        setLangTab('en');
      } else if (errors.titleDe || errors.bodyDe) {
        setLangTab('de');
      }

      setError(
        `Please fill in the following required fields: ${missing.join(' , ')}`
      );
    }

    return isValid;
  };

  /**
   * Handle submit.
   * targetStatus: 'draft' or 'published'
   * - When called from form submit → event is present.
   * - When called from "Save as Draft" → event can be null.
   */
  const handleSubmit = async (event, targetStatus = 'published') => {
    if (event) {
      event.preventDefault();
    }

    const isDraft = targetStatus === 'draft';

    if (!validateForm(isDraft)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      let finalImageUrl = '';

      if (imageFile) {
        // Upload image to S3
        const formData = new FormData();
        formData.append('image', imageFile);
        
        const uploadResponse = await authAxios.post('/upload/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        finalImageUrl = uploadResponse.data.imageUrl;
      } else if (
        previewImage &&
        typeof previewImage === 'string' &&
        !previewImage.startsWith('blob:')
      ) {
        finalImageUrl = previewImage;
      }

      const newsData = {
        title_en: titleEn,
        body_en: bodyEn,
        title_de: titleDe,
        body_de: bodyDe,
        image_url: finalImageUrl,
        status: targetStatus, // 👈 send 'draft' or 'published' to backend
      };

      if (id) {
        await authAxios.put(`/news/${id}`, newsData);
      } else {
        await authAxios.post('/news', newsData);
      }

      navigate('/news');
    } catch (err) {
      console.error('Error saving news item:', err);
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to save news item. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const clearImage = () => {
    setPreviewImage(null);
    setImageFile(null);
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Helpers for current language state
  const currentTitle = langTab === 'en' ? titleEn : titleDe;
  const currentBody = langTab === 'en' ? bodyEn : bodyDe;
  const currentTitleError =
    langTab === 'en' ? formErrors.titleEn : formErrors.titleDe;
  const currentBodyError =
    langTab === 'en' ? formErrors.bodyEn : formErrors.bodyDe;

  const setCurrentTitle = (val) => {
    if (langTab === 'en') setTitleEn(val);
    else setTitleDe(val);
  };

  const setCurrentBody = (val) => {
    if (langTab === 'en') setBodyEn(val);
    else setBodyDe(val);
  };

  return (
    <Box
      component="form"
      onSubmit={(e) => handleSubmit(e, 'published')} // default submit = publish
      noValidate
      sx={{
        width: '100%',
        maxWidth: '1200px',
        mx: 'auto',
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 4 },
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={() => navigate('/news')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          {id ? 'Edit News Item' : 'Create News Item'}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Language Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs value={langTab} onChange={handleLangTabChange} sx={{ mb: 1 }}>
          <Tab value="en" label="English" />
          <Tab value="de" label="German" />
        </Tabs>
        <Typography variant="body2" color="text.secondary">
          You are editing the{' '}
          <strong>{langTab === 'en' ? 'English' : 'German'}</strong> version.
        </Typography>
      </Box>

      <Box sx={{ width: '100%' }}>
        {/* Title per language */}
        <TextField
            required
            fullWidth
            id={`title-${langTab}`}
            label={langTab === 'en' ? 'Title (English)' : 'Title (German)'}
            name="title"
            autoFocus
            value={currentTitle}
            onChange={(e) => setCurrentTitle(e.target.value)}
            disabled={loading}
            error={!!currentTitleError}
            helperText={currentTitleError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TitleIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          {/* Body per language with Write/Preview */}
          <Box sx={{ mb: 3 }}>
            <Tabs
              value={contentTab}
              onChange={handleContentTabChange}
              sx={{ mb: 2 }}
            >
              <Tab label="Write" />
              <Tab label="Preview" />
            </Tabs>

            {contentTab === 0 ? (
              <TextField
                required
                fullWidth
                id={`body-${langTab}`}
                label={
                  langTab === 'en'
                    ? 'Content (English, Markdown supported)'
                    : 'Content (German, Markdown supported)'
                }
                name="body"
                multiline
                rows={20}
                value={currentBody}
                onChange={(e) => setCurrentBody(e.target.value)}
                disabled={loading}
                error={!!currentBodyError}
                helperText={
                  currentBodyError ||
                  'Use Markdown syntax for formatting'
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{ alignSelf: 'flex-start', mt: 1.5 }}
                    >
                      <DescriptionIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  minHeight: '300px',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}
              >
                {currentBody ? (
                  <ReactMarkdown>{currentBody}</ReactMarkdown>
                ) : (
                  <Typography
                    color="text.secondary"
                    sx={{ fontStyle: 'italic' }}
                  >
                    {langTab === 'en'
                      ? 'Preview will appear here...'
                      : 'Die Vorschau erscheint hier...'}
                  </Typography>
                )}
              </Paper>
            )}
          </Box>

          {/* Image upload */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Image
            </Typography>

            <Paper
              {...getRootProps()}
              elevation={0}
              sx={{
                p: 3,
                border: `2px dashed ${
                  isDragActive
                    ? theme.palette.primary.main
                    : theme.palette.divider
                }`,
                borderRadius: 1,
                bgcolor: isDragActive
                  ? alpha(theme.palette.primary.main, 0.05)
                  : 'background.paper',
                cursor: 'pointer',
                textAlign: 'center',
                mb: 2,
              }}
            >
              <input {...getInputProps()} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <ImageIcon
                  sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }}
                />
                {isDragActive ? (
                  <Typography>Drop the image here...</Typography>
                ) : (
                  <Typography>
                    Drag and drop an image here, or click to select
                  </Typography>
                )}
              </Box>
            </Paper>

            {previewImage && (
              <Box sx={{ mt: 2, position: 'relative' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={previewImage}
                    alt="Preview"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        'https://via.placeholder.com/800x400?text=Invalid+Image';
                    }}
                    sx={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                    }}
                  />
                </Paper>
                <IconButton
                  onClick={clearImage}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
          </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ width: '100%', mt: 3, borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Footer buttons */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          onClick={() => navigate('/news')}
          disabled={loading}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>

        {/* Save as Draft */}
        <Button
          type="button"
          variant="outlined"
          disabled={loading}
          onClick={() => handleSubmit(null, 'draft')}
          sx={{ borderRadius: 2 }}
        >
          Save as Draft
        </Button>

        {/* Publish / Save Changes */}
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={<SaveIcon />}
          sx={{
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
          }}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : id ? (
            'Save & Publish'
          ) : (
            'Publish'
          )}
        </Button>
      </Box>
    </Box>
  );
}

export default NewsForm;
